import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  Container,
  Button,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import HomeIcon from '@mui/icons-material/Home';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../../context/SessionContext';
import { getInitials, getContrastTextColor } from '../../utils/colorUtils';
import './PlayerStats.scss';

const PlayerStats = () => {
  const navigate = useNavigate();
  const { sessions } = useSession();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('totalProfit');
  const [sortDirection, setSortDirection] = useState('desc');
  const [viewMode, setViewMode] = useState('table');

  // Aggregate player data across all sessions
  const playerStatsData = useMemo(() => {
    const aggregatedData = new Map();

    // Iterate through all sessions
    sessions.forEach(session => {
      try {
        // Get player data for this session
        const sessionPlayersData = localStorage.getItem(`pokerPlayers_${session.id}`);
        if (sessionPlayersData) {
          const sessionPlayers = JSON.parse(sessionPlayersData);
          
          sessionPlayers.forEach(player => {
            const playerName = player.name;
            const buyIn = player.totalBuyIn || 0;
            const cashOut = player.totalCashOut || 0;
            const profit = cashOut - buyIn;
            
            if (aggregatedData.has(playerName)) {
              const existing = aggregatedData.get(playerName);
              aggregatedData.set(playerName, {
                ...existing,
                totalBuyIn: existing.totalBuyIn + buyIn,
                totalCashOut: existing.totalCashOut + cashOut,
                totalProfit: existing.totalProfit + profit,
                sessionsPlayed: existing.sessionsPlayed + 1,
                sessionIds: [...existing.sessionIds, session.id],
                wins: existing.wins + (profit > 0 ? 1 : 0),
                losses: existing.losses + (profit < 0 ? 1 : 0),
                breakevens: existing.breakevens + (profit === 0 && buyIn > 0 ? 1 : 0)
              });
            } else {
              aggregatedData.set(playerName, {
                name: playerName,
                profileColor: player.profileColor,
                totalBuyIn: buyIn,
                totalCashOut: cashOut,
                totalProfit: profit,
                sessionsPlayed: 1,
                sessionIds: [session.id],
                wins: profit > 0 ? 1 : 0,
                losses: profit < 0 ? 1 : 0,
                breakevens: profit === 0 && buyIn > 0 ? 1 : 0
              });
            }
          });
        }
      } catch (error) {
        console.error(`Error processing session ${session.id}:`, error);
      }
    });

    return Array.from(aggregatedData.values());
  }, [sessions]);

  // Filter and sort players
  const filteredPlayers = useMemo(() => {
    let filtered = playerStatsData.filter(player =>
      player.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort players
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'sessionsPlayed':
          comparison = a.sessionsPlayed - b.sessionsPlayed;
          break;
        case 'wins':
          comparison = a.wins - b.wins;
          break;
        case 'losses':
          comparison = a.losses - b.losses;
          break;
        case 'winRate':
          const aWinRate = a.sessionsPlayed > 0 ? (a.wins / a.sessionsPlayed) : 0;
          const bWinRate = b.sessionsPlayed > 0 ? (b.wins / b.sessionsPlayed) : 0;
          comparison = aWinRate - bWinRate;
          break;
        case 'totalBuyIn':
          comparison = a.totalBuyIn - b.totalBuyIn;
          break;
        case 'totalCashOut':
          comparison = a.totalCashOut - b.totalCashOut;
          break;
        case 'totalProfit':
        default:
          comparison = a.totalProfit - b.totalProfit;
          break;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [playerStatsData, searchTerm, sortBy, sortDirection]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const totalPlayers = filteredPlayers.length;
    const totalSessions = sessions.length;
    const totalWins = filteredPlayers.reduce((sum, player) => sum + player.wins, 0);
    const totalLosses = filteredPlayers.reduce((sum, player) => sum + player.losses, 0);
    const totalProfit = filteredPlayers.reduce((sum, player) => sum + player.totalProfit, 0);
    const topWinner = filteredPlayers.length > 0 ? filteredPlayers[0] : null;

    return {
      totalPlayers,
      totalSessions,
      totalWins,
      totalLosses,
      totalProfit,
      topWinner
    };
  }, [filteredPlayers, sessions]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const calculateWinRate = (wins, sessionsPlayed) => {
    return sessionsPlayed > 0 ? ((wins / sessionsPlayed) * 100).toFixed(1) : '0.0';
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to descending for most columns, ascending for name
      setSortBy(column);
      setSortDirection(column === 'name' ? 'asc' : 'desc');
    }
  };

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const SortableTableCell = ({ column, children, align = 'left' }) => (
    <TableCell 
      align={align}
      className={`sortable-header ${sortBy === column ? 'active' : ''}`}
      onClick={() => handleSort(column)}
    >
      <Box className="header-content" data-align={align}>
        {children}
        {sortBy === column && (
          sortDirection === 'asc' ? 
            <ArrowUpwardIcon className="sort-icon" /> : 
            <ArrowDownwardIcon className="sort-icon" />
        )}
      </Box>
    </TableCell>
  );

  const PlayerCard = ({ player }) => (
    <Card className={`player-stats-card ${player.totalProfit > 0 ? 'winner' : player.totalProfit < 0 ? 'loser' : 'neutral'}`}>
      <CardContent>
        <Box className="player-header">
          <Avatar
            sx={{
              bgcolor: player.profileColor,
              color: getContrastTextColor(player.profileColor),
              width: 48,
              height: 48,
              fontSize: '1.25rem',
              fontWeight: 600
            }}
          >
            {getInitials(player.name)}
          </Avatar>
          <Box className="player-info">
            <Typography variant="h6" className="player-name">
              {player.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {player.sessionsPlayed} session{player.sessionsPlayed !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <Box className="profit-display">
            <Typography variant="h6" className={`profit-amount ${player.totalProfit > 0 ? 'positive' : player.totalProfit < 0 ? 'negative' : 'neutral'}`}>
              {formatCurrency(player.totalProfit)}
            </Typography>
          </Box>
        </Box>

        <Box className="stats-grid">
          <Box className="stat-item">
            <Typography variant="caption" color="text.secondary">Win Rate</Typography>
            <Typography variant="body2" className="stat-value">
              {calculateWinRate(player.wins, player.sessionsPlayed)}%
            </Typography>
          </Box>
          <Box className="stat-item">
            <Typography variant="caption" color="text.secondary">Wins</Typography>
            <Typography variant="body2" className="stat-value wins">
              {player.wins}
            </Typography>
          </Box>
          <Box className="stat-item">
            <Typography variant="caption" color="text.secondary">Losses</Typography>
            <Typography variant="body2" className="stat-value losses">
              {player.losses}
            </Typography>
          </Box>
          <Box className="stat-item">
            <Typography variant="caption" color="text.secondary">Total Buy-in</Typography>
            <Typography variant="body2" className="stat-value">
              {formatCurrency(player.totalBuyIn)}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="lg" className="player-stats fade-in">
      {/* Header */}
      <Box className="stats-header">
        <Box className="header-content">
          <Button
            startIcon={<HomeIcon />}
            onClick={() => navigate('/')}
            className="back-button"
          >
            Back to Sessions
          </Button>
          <Box className="title-section">
            <EmojiEventsIcon className="title-icon" />
            <Typography variant="h2" className="page-title">
              Player Statistics
            </Typography>
          </Box>
        </Box>
        <Typography variant="h6" color="text.secondary" className="page-subtitle">
          Track wins, losses, and performance across all sessions
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} className="summary-section">
        <Grid item xs={12} sm={6} md={3}>
          <Card className="summary-card">
            <CardContent>
              <Box className="summary-content">
                <TrendingUpIcon className="summary-icon positive" />
                <Box>
                  <Typography variant="h4" className="summary-number">
                    {summaryStats.totalPlayers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Players
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className="summary-card">
            <CardContent>
              <Box className="summary-content">
                <EmojiEventsIcon className="summary-icon winner" />
                <Box>
                  <Typography variant="h4" className="summary-number">
                    {summaryStats.totalWins}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Wins
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className="summary-card">
            <CardContent>
              <Box className="summary-content">
                <TrendingDownIcon className="summary-icon negative" />
                <Box>
                  <Typography variant="h4" className="summary-number">
                    {summaryStats.totalLosses}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Losses
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={`summary-card ${summaryStats.totalProfit > 0 ? 'profit' : summaryStats.totalProfit < 0 ? 'loss' : 'neutral'}`}>
            <CardContent>
              <Box className="summary-content">
                {summaryStats.totalProfit > 0 ? (
                  <TrendingUpIcon className="summary-icon positive" />
                ) : (
                  <TrendingDownIcon className="summary-icon negative" />
                )}
                <Box>
                  <Typography variant="h4" className="summary-number">
                    {formatCurrency(Math.abs(summaryStats.totalProfit)).replace('$', '')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Net {summaryStats.totalProfit >= 0 ? 'Profit' : 'Loss'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Controls */}
      <Box className="controls-section">
        <Box className="left-controls">
          <TextField
            placeholder="Search players..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            className="search-field"
          />
          <Typography variant="body2" color="text.secondary" className="sort-info">
            Click column headers to sort
          </Typography>
        </Box>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          className="view-toggle"
          size="small"
        >
          <ToggleButton value="cards" aria-label="card view">
            <ViewModuleIcon />
          </ToggleButton>
          <ToggleButton value="table" aria-label="table view">
            <TableRowsIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Players Display */}
      {filteredPlayers.length === 0 ? (
        <Paper className="empty-state">
          <EmojiEventsIcon className="empty-icon" />
          <Typography variant="h5" className="empty-title">
            No player statistics yet
          </Typography>
          <Typography variant="body1" color="text.secondary" className="empty-subtitle">
            Player statistics will appear here once you have sessions with players
          </Typography>
        </Paper>
      ) : viewMode === 'cards' ? (
        <Grid container spacing={3} className="players-grid">
          {filteredPlayers.map((player) => (
            <Grid item xs={12} sm={6} md={4} key={player.name}>
              <PlayerCard player={player} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <TableContainer component={Paper} className="players-table">
          <Table>
            <TableHead>
              <TableRow>
                <SortableTableCell column="name">Player</SortableTableCell>
                <SortableTableCell column="sessionsPlayed" align="right">Sessions</SortableTableCell>
                <SortableTableCell column="wins" align="right">Wins</SortableTableCell>
                <SortableTableCell column="losses" align="right">Losses</SortableTableCell>
                <SortableTableCell column="winRate" align="right">Win Rate</SortableTableCell>
                <SortableTableCell column="totalBuyIn" align="right">Total Buy-in</SortableTableCell>
                <SortableTableCell column="totalCashOut" align="right">Total Cash-out</SortableTableCell>
                <SortableTableCell column="totalProfit" align="right">Profit/Loss</SortableTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPlayers.map((player) => (
                <TableRow key={player.name} className="player-row">
                  <TableCell>
                    <Box className="player-cell">
                      <Avatar
                        sx={{
                          bgcolor: player.profileColor,
                          color: getContrastTextColor(player.profileColor),
                          width: 32,
                          height: 32,
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          mr: 2
                        }}
                      >
                        {getInitials(player.name)}
                      </Avatar>
                      <Typography variant="body2" className="player-name">
                        {player.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">{player.sessionsPlayed}</TableCell>
                  <TableCell align="right">
                    <Chip label={player.wins} size="small" className="wins-chip" />
                  </TableCell>
                  <TableCell align="right">
                    <Chip label={player.losses} size="small" className="losses-chip" />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" className="win-rate">
                      {calculateWinRate(player.wins, player.sessionsPlayed)}%
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{formatCurrency(player.totalBuyIn)}</TableCell>
                  <TableCell align="right">{formatCurrency(player.totalCashOut)}</TableCell>
                  <TableCell align="right">
                    <Typography 
                      variant="body2" 
                      className={`profit-cell ${player.totalProfit > 0 ? 'positive' : player.totalProfit < 0 ? 'negative' : 'neutral'}`}
                    >
                      {formatCurrency(player.totalProfit)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default PlayerStats;
